import type { Order } from "../../../../store/types";

interface UnbatchedOrdersTableProps {
  orders: Order[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-amber-50 text-amber-600",
  confirmed: "bg-emerald-50 text-emerald-600",
  cancelled: "bg-red-50 text-red-500",
  completed: "bg-[#C8B8A6]/15 text-[#2C2C2C]/50",
};

function OrderStatusBadge({ status }: { status: string }) {
  const cls = STATUS_COLORS[status] ?? "bg-[#D9D9D9]/50 text-[#2C2C2C]/40";
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light capitalize ${cls}`}>
      {status}
    </span>
  );
}

export default function UnbatchedOrdersTable({ orders }: UnbatchedOrdersTableProps) {
  const headers = ["Order #", "City", "Shoot Date", "P Units", "Status", "Created"];

  return (
    <div className="bg-white rounded-lg border border-[#D9D9D9]/60 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#D9D9D9]/60 bg-[#F7F6F3]">
            {headers.map((h) => (
              <th key={h} className="text-left px-5 py-3 text-xs uppercase tracking-wider text-[#2C2C2C]/40 font-light">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orders.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="text-center py-12 text-[#2C2C2C]/30 text-sm font-light">
                All orders are assigned to batches
              </td>
            </tr>
          ) : (
            orders.map((order) => (
              <tr
                key={order.id}
                className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors"
              >
                <td className="px-5 py-3">
                  <span className="text-xs font-mono text-[#2C2C2C]/60">{order.order_number}</span>
                </td>
                <td className="px-5 py-3 text-sm text-[#2C2C2C]">
                  {order.selected_city || "—"}
                </td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/60">
                  {order.shoot_date
                    ? new Date(order.shoot_date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "—"}
                </td>
                <td className="px-5 py-3">
                  <span className="inline-flex items-center gap-1.5 text-sm font-light text-[#2C2C2C]">
                    <span className="w-5 h-5 flex items-center justify-center rounded bg-[#C8B8A6]/20 text-[#C8B8A6] text-xs font-medium">
                      {order.p_units ?? 0}
                    </span>
                    <span className="text-[#2C2C2C]/40 text-xs">P</span>
                  </span>
                </td>
                <td className="px-5 py-3">
                  <OrderStatusBadge status={order.order_status} />
                </td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/40">
                  {new Date(order.created_at).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
